import { useContext, useMemo } from 'react'

import useBedbankRates, { useBedbankRatesForIndividualRooms } from 'hooks/useBedbankRates'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { getPromotionMinLos, isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { useAppSelector } from './reduxHooks'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { getBedbankPackageViews } from 'lib/hotels/bedbankUtils'
import { initialOfferPageRooms, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject, countWhere, max, min } from 'lib/array/arrayUtils'

function useBedbankPackageViews(
  offer: App.BedbankOffer | undefined,
  persistPromo: boolean = false,
): [Array<App.BedbankOfferPackageView>, boolean] {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    rooms = initialOfferPageRooms,
    flights: isFlightsSelected,
  } = offerPageState

  let checkIn = offerPageState.checkInDate
  let checkOut = offerPageState.checkOutDate
  let duration = offerPageState.duration

  const [{ hotelOnlyRates, hotelBundleRates }, fetchingRates] = useBedbankRates(offer?.id, rooms, checkIn, checkOut)

  const promotionMinLos = useMemo(() => getPromotionMinLos(offer), [offer])
  const isCalendarEnabled = useAppSelector((state) => isFlashCalendarEnabled(offerPageState, state))
  const promotions = useBedbankRoomRatePromotions(offer?.promotions, checkIn, checkOut)

  if (persistPromo && duration < promotionMinLos) {
    checkIn = undefined
    checkOut = undefined
    duration = promotionMinLos
  }

  const result = useMemo(() => {
    return getBedbankPackageViews(offer, rooms, duration, checkIn, checkOut, hotelOnlyRates, hotelBundleRates, promotionMinLos, isCalendarEnabled, promotions, isFlightsSelected)
  }, [offer, promotions, checkIn, checkOut, hotelOnlyRates, hotelBundleRates, promotionMinLos, duration, isCalendarEnabled, rooms, isFlightsSelected])

  return [result, fetchingRates]
}

export default useBedbankPackageViews

export function useBedbankPackageViewsForIndividualRooms(
  offer: App.BedbankOffer | undefined,
  rooms: Array<OfferPageRoom>,
): [Array<Array<App.BedbankOfferPackageView>>, boolean] {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    duration,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    flights: isFlightsSelected,
  } = offerPageState
  const [ratesForRooms, fetchingRates] = useBedbankRatesForIndividualRooms(offer?.id, rooms, checkIn, checkOut)

  const promotionMinLos = useMemo(() => getPromotionMinLos(offer), [offer])
  const isCalendarEnabled = useAppSelector((state) => isFlashCalendarEnabled(offerPageState, state))
  const promotions = useBedbankRoomRatePromotions(offer?.promotions, checkIn, checkOut)

  const result = useMemo(() => {
    return rooms.map((room, i) => {
      const { hotelOnlyRates, hotelBundleRates } = ratesForRooms[i]
      return getBedbankPackageViews(offer, [room], duration, checkIn, checkOut, hotelOnlyRates, hotelBundleRates, promotionMinLos, isCalendarEnabled, promotions, isFlightsSelected)
    })
  }, [offer, promotions, checkIn, checkOut, ratesForRooms, promotionMinLos, duration, isCalendarEnabled, rooms, isFlightsSelected])

  return [result, fetchingRates]
}

export function useBedbankPackageViewsForIndividualRoom(
  offer: App.BedbankOffer | undefined,
  room: OfferPageRoom,
): [Array<App.BedbankOfferPackageView>, boolean] {
  const rooms = useMemo(() => [room], [room])
  const [ratesByRoom, fetchingRates] = useBedbankPackageViewsForIndividualRooms(offer, rooms)
  return [ratesByRoom[0], fetchingRates]
}

export function useIsRoomTypePartiallyAvailable(
  offer: App.BedbankOffer | undefined,
  rooms: Array<OfferPageRoom>,
  roomTypeId: string,
) {
  const [packageViewsByRoom, packageViewsLoading] = useBedbankPackageViewsForIndividualRooms(offer, rooms)

  return useMemo(() => {
    if (packageViewsLoading) {
      return false
    }

    const packageViewForThisRoomTypeByRoom = packageViewsByRoom.map((packageViews) => packageViews.find(view => view.package.roomId === roomTypeId))

    const roomCountWithThisTypeAvailable = countWhere(
      packageViewForThisRoomTypeByRoom,
      (packageView) => !!packageView,
    )

    if (roomCountWithThisTypeAvailable === 0) {
      return false // Room type isn't available at all
    } else if (roomCountWithThisTypeAvailable < rooms.length) {
      return true // Only available for some rooms
    } else {
      // The room type is an option for all rooms - BUT, the inventory might not be enough to satisfy the quantity of rooms
      const maxAvailability = min(
        packageViewForThisRoomTypeByRoom.map(view => {
          if (!view) { return 0 }
          return max(view.rates.map(rate => rate.availableRooms ?? 0))
        }),
      ) ?? 0
      return maxAvailability < rooms.length
    }
  }, [packageViewsByRoom, packageViewsLoading, roomTypeId, rooms.length])
}

export type RoomTypeAvailabilityStatus = 'available' | 'unavailable' | 'partiallyAvailable' | 'loading'

export function useRoomTypeAvailabilityStatuses(
  offer: App.BedbankOffer | undefined,
  rooms: Array<OfferPageRoom>,
  packageViews: Array<App.BedbankOfferPackageView>,
): Record<string, RoomTypeAvailabilityStatus> {
  const [packageViewsByRoom, packageViewsLoading] = useBedbankPackageViewsForIndividualRooms(offer, rooms)

  return useMemo(
    () => determineRoomTypeAvailabilityStatuses(packageViews, packageViewsByRoom, packageViewsLoading),
    [packageViewsByRoom, packageViewsLoading, packageViews],
  )
}

export function determineRoomTypeAvailabilityStatuses(
  packageViews: Array<App.BedbankOfferPackageView>,
  packageViewsByRoom: Array<Array<App.BedbankOfferPackageView>>,
  packageViewsLoading: boolean,
): Record<string, RoomTypeAvailabilityStatus> {
  return arrayToObject(
    packageViews,
    pkg => pkg.package.roomId,
    (pkg): RoomTypeAvailabilityStatus => {
      if (packageViewsLoading) {
        return 'loading'
      }

      if (pkg.available) {
        return 'available'
      }

      const packageViewForThisRoomTypeByRoom = packageViewsByRoom.map((packageViews) => packageViews.find(view => view.package.roomId === pkg.package.roomId && view.available))

      const roomCountWithThisTypeAvailable = countWhere(
        packageViewForThisRoomTypeByRoom,
        (packageView) => !!packageView,
      )

      if (roomCountWithThisTypeAvailable === 0) {
        return 'unavailable'
      } else {
        return 'partiallyAvailable'
      }
    },
  )
}
